import {
  Input as ChakraInput,
  InputProps as ChakraInputProps,
  Textarea as ChakraTextarea,
} from '@chakra-ui/react'
import styled from '@emotion/styled'
import * as React from 'react'

import { useBgColorCtx } from '~theme/BgColorProvider'
import { useFgColorValue } from '~theme/fgColor'
import { TInputColorKey } from '~theme/typings'
import { getActiveColorFullPath } from '~theme/utils'
import { ButtonIcon } from '~ui/components/buttons/ButtonIcon/ButtonIcon'
import { Icon } from '~ui/components/icons/Icon'
import { Box, Flex } from '~ui/components/layout/Box'
import { Stack } from '~ui/components/layout/Stack'
import { cn } from '~ui/libs/classnames'
import { fontSizes, fontStyles, TFontStyle } from '~ui/styleGuide/fontStyles'
import {
  getBottomPadding,
  getLeftPadding,
  getRightPadding,
  getSpacingValue,
  getTopPadding,
} from '~ui/styleGuide/spaces'
import { durations } from '~ui/styleGuide/styles'
import { useHoverState } from '~ui/utils/useHoverState'

type TChakraProps = Pick<
  ChakraInputProps,
  | 'placeholder'
  | 'autoComplete'
  | 'isDisabled'
  | 'isRequired'
  | 'id'
  | 'type'
  | 'inputMode'
  | 'name'
  | 'step'
  | 'min'
  | 'max'
> & {
  // placed here because of type conflict between input and textarea events
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>
  onKeyDown?: React.KeyboardEventHandler<HTMLTextAreaElement | HTMLInputElement>
}

type TProps = NoChildren &
  TestingProps &
  TChakraProps & {
    /**
     * the text that should be rendered inside the input and that the user can edit
     */
    value: string
    /**
     * key of radius,
     * or an object with separate key for each corner
     */
    radius?: PropsOf<typeof Box>['radius']
    /**
     * space inside the input,
     * can be single space key,
     * object with `horizontal` and `vertical` attributes
     * or separate values for each side `top`, `right`, `bottom`, `left
     */
    padding?: PropsOf<typeof Box>['padding']
    /**
     * background color of the input area
     * also used to determine the text & icon colors
     */
    bgColor?: TInputColorKey
    /**
     * key of the predefined font styles
     * might set attributes like font family, weight, casing, ...
     */
    fontStyle?: TFontStyle
    /**
     * the text rendered in place of the real value when the input is empty
     */
    placeholder?: string
    /**
     * highlights inputs that are not in a valid state (eg. red outline)
     */
    isInvalid?: boolean
    /**
     * number of lines that should be visible in the input
     * sets input height accordingly
     * for values > 1, the textarea is used internally instead of an input
     */
    numberOfLines?: number
    /**
     * callback called whenever user action changes the input value
     * provides the new input value directly, not the change event!
     */
    onChange: (newValue: string) => void
    /**
     * props for icon rendered on the left side of the input
     */
    leftIcon?: PropsOf<typeof Icon>['icon']
    /**
     * props for icon rendered on the right side of the input
     */
    rightIcon?: {
      icon: PropsOf<typeof ButtonIcon>['icon']
      contrast?: PropsOf<typeof ButtonIcon>['iconContrast']
      onClick?: PropsOf<typeof ButtonIcon>['onClick']
    }
    /**
     * space between icons and the input
     * key of predefined spaces, defines the space between items in the Stack
     */
    iconSpacing?: PropsOf<typeof Stack>['spacing']
  }

const StyledBox = styled(Box)`
  transition: ${durations.short}ms all;

  &.invalid > div {
    border-color: ${({ theme }) => theme.specialColors.error};
  }

  /* define bellow other styles so the focus is always visible when needed */
  &:focus-within > div {
    border-color: ${({ theme }) => theme.specialColors.info};
  }
`

// might become props or responsive later
const fontSize = fontSizes.text.m16
const lineHeightInput = 1.2
const lineHeightTextarea = 1.5

// unify browser default styles
// https://stackoverflow.com/a/19871806
const PLACEHOLDER_OPACITY = 1

/**
 * prevent Safari browser auto-filling whole phone number also to the phone-prefix input
 * workaround based on the: https://bytes.grubhub.com/disabling-safari-autofill-for-a-single-line-address-input-b83137b5b1c7
 * string 'search' is the key in this solution
 *
 * use as a value of the `name` prop
 *
 * @example
 * <Input name={PREVENT_SAFARI_AUTOFILL} />
 */
export const PREVENT_SAFARI_AUTOFILL = `searchSafari`

/**
 * basic text input with some styles, custom focus outline & possibly an icon
 */
export const Input: React.FC<TProps> = ({
  leftIcon,
  rightIcon,
  bgColor,
  radius = `small`,
  isInvalid = false,
  isDisabled = false,
  numberOfLines = 1,
  iconSpacing = `medium`,
  padding = `medium`,
  dataTestId,
  ...inputProps
}) => {
  const { bgColor: parentBgColor, dividerColors } = useBgColorCtx()
  const { isHovered, setHoverStart, setHoverEnd } = useHoverState()

  // get the bgColor, `bgColor` from props has the highest priority
  // if not provided, use the parent bgColor and determine the input bgColor based on it
  const isOnCard = parentBgColor === `bgColors.card`
  const autoBgColor = isOnCard ? `inputOnCard` : `inputOnPrimary`
  const bgColorForActive = bgColor ?? autoBgColor

  const bgColorKey = React.useMemo(
    () => getActiveColorFullPath({ isHovered, isDisabled, bgColor: bgColorForActive }),
    [bgColorForActive, isDisabled, isHovered],
  )

  const lineHeight = numberOfLines > 1 ? lineHeightTextarea : lineHeightInput

  const leftPadding = getLeftPadding(padding)
  const rightPadding = getRightPadding(padding)
  const topPadding = getTopPadding(padding)
  const bottomPadding = getBottomPadding(padding)

  return (
    <StyledBox
      radius={radius}
      bgColor={bgColorKey}
      onPointerLeave={setHoverEnd}
      onPointerEnter={setHoverStart}
      className={cn({ invalid: isInvalid })}
    >
      <Flex
        radius={radius}
        gap={iconSpacing}
        alignItems={`center`}
        border={`1px solid ${dividerColors.low}`}
        padding={{
          left: leftIcon ? leftPadding : `none`,
          right: rightIcon ? rightPadding : `none`,
          top: `none`,
          bottom: `none`,
        }}
      >
        {leftIcon && (
          <Icon icon={leftIcon} contrast={`low`} iconSize={`calc(${lineHeight} * ${fontSize})`} />
        )}
        <InputElement
          {...inputProps}
          isDisabled={isDisabled}
          dataTestId={dataTestId}
          numberOfLines={numberOfLines}
          lineHeight={lineHeight}
          padding={{
            left: leftIcon ? `none` : leftPadding,
            right: rightIcon ? `none` : rightPadding,
            top: topPadding,
            bottom: bottomPadding,
          }}
        />
        {rightIcon && (
          <ButtonIcon
            icon={rightIcon.icon}
            iconSize={`calc(${lineHeight} * ${fontSize})`}
            variant={`ghost`}
            padding={`none`}
            shadow={`none`}
            iconContrast={rightIcon.contrast}
            onClick={rightIcon.onClick}
          />
        )}
      </Flex>
    </StyledBox>
  )
}

type TInputElementProps = Omit<TProps, 'numberOfLines'> &
  TestingProps & {
    numberOfLines: Defined<TProps['numberOfLines']>
    lineHeight: number
  }

/**
 * the actual input/textarea tag
 * extracted from the Input component to be able to use the closest BgColorProvider
 * that is rendered inside one of the Boxes rendered inside the Input component
 */
const InputElement: React.FC<TInputElementProps> = ({
  value,
  onChange,
  isInvalid,
  isDisabled,
  lineHeight,
  numberOfLines,
  fontStyle = `regular`,
  dataTestId,
  padding,
  type = `text`,
  ...chakraProps
}) => {
  const textColorValue = useFgColorValue({ contrast: `normal` })
  const placeholderColorValue = useFgColorValue({ contrast: `placeholder` })

  const paddingValue = padding && getSpacingValue(padding)

  const Element = numberOfLines > 1 ? ChakraTextarea : ChakraInput
  return (
    <Element
      {...chakraProps}
      {...fontStyles[fontStyle]}
      flexGrow={1}
      type={type}
      value={value}
      flexShrink={1}
      width={`100%`} // keep the input from overflowing the Stack container
      border={`none`}
      resize={`none`}
      padding={paddingValue}
      outline={`none`}
      fontSize={fontSize}
      isInvalid={isInvalid}
      lineHeight={lineHeight}
      isDisabled={isDisabled}
      background={`transparent`}
      color={textColorValue}
      _placeholder={{ color: placeholderColorValue, opacity: PLACEHOLDER_OPACITY }} // placeholder styles
      onChange={(event) => onChange(event.target.value)}
      height={`calc(${numberOfLines} * ${lineHeight} * ${fontSize})`}
      boxSizing={`content-box`}
      // override Safari default styles for disabled inputs
      // https://stackoverflow.com/questions/262158/disabled-input-text-color-on-ios
      sx={{
        '&[disabled]': {
          WebkitTextFillColor: textColorValue,
          opacity: 1,
        },
      }}
      data-testid={dataTestId}
    />
  )
}
